import type { CurriculumTier, Trin } from '../../types';
import {
  filterWords,
  fromChunks,
  fromLetters,
  literal,
  round,
} from './generators';

function g(
  n: number,
  data: Omit<Trin, 'id' | 'tierId' | 'trinNumber'>,
): Trin {
  return {
    id: `grundniveau-trin-${n}`,
    tierId: 'grundniveau',
    trinNumber: n,
    ...data,
  };
}

const LEVEL_6_WORDS = [
  'ad', 'af', 'da', 'ja', 'lad', 'sad', 'sal', 'dal', 'fald', 'kald',
  'flak', 'skal', 'falsk', 'klask', 'sjask', 'alfa', 'saks', 'fad', 'laks',
  'læs', 'dæk', 'sæk', 'kælk', 'flæsk',
];
export const LEVEL_6_KEYS = ['a', 's', 'd', 'f', 'j', 'k', 'l', 'æ'];
export const LEVEL_6_VALID = filterWords(LEVEL_6_WORDS, LEVEL_6_KEYS);

const GRUNDNIVEAU_TRINS: Trin[] = [
  g(1, {
    title: 'Ankerpunkter',
    description: 'Find pegefingrenes hvileposition på F og J.',
    goal: 'Pegefingrene skal kunne finde F og J uden at kigge.',
    unlockedKeys: ['f', 'j'],
    newKeys: ['f', 'j'],
    rounds: [
      round({ id: 'gn1-r1', title: 'Find ankrene', purpose: 'Mærk hævningerne på F og J. Tryk roligt.', mode: 'guided', generate: literal('f j f j f j f j f j f j f j f j f j f j') }),
      round({ id: 'gn1-r2', title: 'Dobbelttryk', purpose: 'Den samme finger to gange i træk — kontrolleret tempo.', mode: 'guided', generate: literal('ff jj ff jj ff jj ff jj ff jj ff jj') }),
      round({ id: 'gn1-r3', title: 'Skift side', purpose: 'Skift mellem venstre og højre pegefinger.', mode: 'recall', generate: literal('fj fj jf jf fj jf fj jf fj jf fj jf') }),
      round({ id: 'gn1-r4', title: 'Bland det sammen', purpose: 'Korte rytmer uden mønster.', mode: 'recall', generate: fromLetters(['f', 'j'], 4, 60) }),
      round({ id: 'gn1-r5', title: 'Mini-udfordring', purpose: 'Ingen ledetråde.', mode: 'challenge', generate: literal('fjjf fjfj jffj jfjf fjjf jffj') }),
    ],
  }),
  g(2, {
    title: 'Langfingre',
    description: 'Tilføj D og K til ankrene.',
    goal: 'Langfingrene rammer D og K uden at flytte hænderne.',
    unlockedKeys: ['f', 'j', 'd', 'k'],
    newKeys: ['d', 'k'],
    rounds: [
      round({ id: 'gn2-r1', title: 'Find ankrene', purpose: 'Genkald fra trin 1.', mode: 'guided', generate: literal('f j f j jf fj f j f j jf fj') }),
      round({ id: 'gn2-r2', title: 'Tilføj langfingrene', purpose: 'D og K som naboer til ankrene.', mode: 'guided', generate: literal('fd jk fd jk df kj fd jk fd jk df kj') }),
      round({ id: 'gn2-r3', title: 'Venstre-højre', purpose: 'Alternér mellem hænderne — find rytmen.', mode: 'recall', generate: literal('fj dk fj dk fd jk fj dk fj dk fd jk') }),
      round({ id: 'gn2-r4', title: 'Genkald uden hjælp', purpose: 'Pres dig selv en lille smule.', mode: 'recall', generate: literal('fdjk jkdf fjdk kdfj fdjk jkdf') }),
      round({ id: 'gn2-r5', title: 'Mini-udfordring', purpose: 'Ingen ledetråde med mindre du gentager fejl.', mode: 'challenge', generate: literal('fj dk fd jk df kj fj dk fd jk') }),
    ],
  }),
  g(3, {
    title: 'Ringfingre',
    description: 'Tilføj S og L. Hænderne er symmetriske.',
    goal: 'Ringfingrene skal lære S (venstre) og L (højre).',
    unlockedKeys: ['f', 'j', 'd', 'k', 's', 'l'],
    newKeys: ['s', 'l'],
    rounds: [
      round({ id: 'gn3-r1', title: 'Nye taster', purpose: 'Mærk S og L — naboer til D og K.', mode: 'guided', generate: literal('s l s l ls sl s l s l ls sl') }),
      round({ id: 'gn3-r2', title: 'Forbind til det kendte', purpose: 'S/L sammen med D/K.', mode: 'guided', generate: literal('sd lk sd lk ds kl sd lk ds kl') }),
      round({ id: 'gn3-r3', title: 'Hold takten', purpose: 'Skriv én tast pr. slag — find et jævnt tempo.', mode: 'recall', generate: literal('fs jl fs jl df kj fs jl df kj'), presentation: { kind: 'metronome', bpm: 55 } }),
      round({ id: 'gn3-r4', title: 'Byg klumper', purpose: 'Tre-tegns klumper, samme rytme.', mode: 'recall', generate: literal('dsf klj dsf klj dsf klj sdf lkj') }),
      round({ id: 'gn3-r5', title: 'Symmetri', purpose: 'Venstre-hånd spejlet til højre.', mode: 'recall', generate: literal('fdsj klkj fdsj klkj sdfj lkjf') }),
      round({ id: 'gn3-r6', title: 'Mini-udfordring', purpose: 'Seks taster, ingen hjælp.', mode: 'challenge', generate: literal('fjdksl ldksfj fjdksl ldksfj') }),
    ],
  }),
  g(4, {
    title: 'Lillefingre',
    description: 'Hele hjemmerækken: a s d f j k l æ.',
    goal: 'Lær pinkernes A og Æ — den fulde danske hjemmerække.',
    unlockedKeys: ['a', 's', 'd', 'f', 'j', 'k', 'l', 'æ'],
    newKeys: ['a', 'æ'],
    rounds: [
      round({ id: 'gn4-r1', title: 'Pinkerne på plads', purpose: 'Aktivér lillefingrene.', mode: 'guided', generate: literal('a æ a æ aæ æa a æ a æ aæ æa') }),
      round({ id: 'gn4-r2', title: 'Forbind til naboer', purpose: 'A med S, Æ med L.', mode: 'guided', generate: literal('as læ as læ sa æl as læ sa æl') }),
      round({ id: 'gn4-r3', title: 'Hold takten', purpose: 'Skriv én tast pr. slag — find et jævnt tempo.', mode: 'recall', generate: literal('af jæ af jæ fa æj af jæ fa æj'), presentation: { kind: 'metronome', bpm: 55 } }),
      round({ id: 'gn4-r4', title: 'Hele rækken', purpose: 'Den fulde hjemmerække som mønster.', mode: 'recall', generate: literal('asdf jklæ asdf jklæ ælkj fdsa') }),
      round({ id: 'gn4-r5', title: 'Blanding', purpose: 'Otte taster, ingen mønster.', mode: 'recall', generate: fromLetters(['a', 's', 'd', 'f', 'j', 'k', 'l', 'æ'], 4, 70) }),
      round({ id: 'gn4-r6', title: 'Mini-udfordring', purpose: 'Spejlsekvenser, ingen hjælp.', mode: 'challenge', generate: literal('asdfjklæ ælkjfdsa asdfjklæ ælkjfdsa') }),
    ],
  }),
  g(5, {
    title: 'Mellemrum',
    description: 'Tommelfingeren lærer mellemrumstasten.',
    goal: 'Korte rytmegrupper med naturligt mellemrum.',
    unlockedKeys: ['a', 's', 'd', 'f', 'j', 'k', 'l', 'æ', ' '],
    newKeys: [' '],
    rounds: [
      round({ id: 'gn5-r1', title: 'Tommelfingeren ind', purpose: 'Enkelt-tegn med mellemrum imellem.', mode: 'guided', generate: literal('f j d k s l a æ f j d k s l a æ') }),
      round({ id: 'gn5-r2', title: 'To-tegns rytme', purpose: 'Korte par adskilt af mellemrum.', mode: 'guided', generate: literal('as df jk læ as df jk læ as df jk læ') }),
      round({ id: 'gn5-r3', title: 'Tre-tegns klumper', purpose: 'Flydende mellemrum.', mode: 'recall', generate: literal('asd fjk læs asd fjk læs sdf lkj') }),
      round({ id: 'gn5-r4', title: 'Flash-genkald', purpose: 'Se ordet kort, skriv det fra hukommelsen.', mode: 'recall', generate: fromChunks(['asd', 'fjk', 'læs', 'dal', 'sak', 'jad', 'kæl', 'lad'], 70), presentation: { kind: 'echo', flashMs: 1500, unit: 'word' } }),
      round({ id: 'gn5-r5', title: 'Stabil rytme', purpose: 'Flow over fart.', mode: 'recall', generate: literal('as df jk læ as df jk læ as df jk læ') }),
      round({ id: 'gn5-r6', title: 'Mini-udfordring', purpose: 'Tilfældige klumper, ingen hjælp.', mode: 'challenge', generate: fromChunks(['asd', 'fjk', 'læs', 'dal', 'sak', 'jad', 'kæl', 'lad'], 80) }),
    ],
  }),
  g(6, {
    title: 'Første danske ord',
    description: 'Rigtige korte danske ord af det du har lært.',
    goal: 'Læs ordene som meningsfulde enheder — ikke som tegn.',
    unlockedKeys: [...LEVEL_6_KEYS, ' '],
    newKeys: [],
    rounds: [
      round({ id: 'gn6-r1', title: 'To-bogstavs-ord', purpose: 'Helt korte rigtige ord.', mode: 'guided', generate: literal('ad af da ja ad af da ja ad af da ja') }),
      round({ id: 'gn6-r2', title: 'Tre-bogstavs-ord', purpose: 'Velkendte ord-former.', mode: 'guided', generate: literal('lad sad sal dal lad sad sal dal lad sad sal dal') }),
      round({ id: 'gn6-r3', title: 'Ord med æ', purpose: 'Træn æ i kontekst.', mode: 'recall', generate: literal('læs dæk sæk læs dæk sæk kælk flæsk læs dæk') }),
      round({ id: 'gn6-r4', title: 'Fire-bogstavs-ord', purpose: 'Længere ord — pas på rytmen.', mode: 'recall', generate: literal('fald kald flak skal fald kald flak skal') }),
      round({ id: 'gn6-r5', title: 'Diktat: korte ord', purpose: 'Lyt til ordet og skriv det uden at se det.', mode: 'recall', generate: fromChunks(['ad', 'af', 'da', 'ja', 'lad', 'sal', 'dal'], 60), presentation: { kind: 'dictation' } }),
      round({ id: 'gn6-r6', title: 'Blandet tekst', purpose: 'Varieret ordforråd.', mode: 'recall', generate: fromChunks(LEVEL_6_VALID, 90) }),
      round({ id: 'gn6-r7', title: 'Mini-udfordring', purpose: 'Kun ordene og dig.', mode: 'challenge', generate: fromChunks(LEVEL_6_VALID, 80) }),
    ],
  }),
];

export const GRUNDNIVEAU_TIER: CurriculumTier = {
  id: 'grundniveau',
  title: 'Grundniveau',
  description:
    'Fundamentet: hjemmerækken, mellemrum og de første danske ord.',
  trins: GRUNDNIVEAU_TRINS,
};
